import { Component, ElementRef, HostListener, inject, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../modules/auth/services/auth.service';
import { AuthApiService } from '../../../modules/auth/services/auth-api.service';
import { ToastNotificationService } from '../../../notifications/services/toast-notification.service';
import { ToastNotificationType } from '../../../notifications/models/toast-notification-type';
import { JwtPayload } from '../../../modules/auth/models/jwt-payload';
import { Permissions } from '../../../modules/auth/permissions/permissions';
import { NavigationService } from '../services/navigation.service';
import { LanguageService, SupportedLanguage } from '../../services/translations/language.service';
import { VendorProfileService } from '../../services/vendor-profile.service';

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, TranslateModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit {

  authService = inject(AuthService);
  authApiService = inject(AuthApiService);
  languageService = inject(LanguageService);
  vendorProfile = inject(VendorProfileService);
  translate = inject(TranslateService);

  toastNotificationService = inject(ToastNotificationService);

  JwtPayload:JwtPayload | null = null;

  sidebarItems = inject(NavigationService).getNavigationItems();
  isDropdownOpen = false;
  isLanguageOpen = false;

  activeLink?: {title: string, icon: string};

  menuClick = output();

  constructor(
    private eRef: ElementRef,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.JwtPayload = this.authService.getTokenData();
    this.vendorProfile.load();

    this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(() => {
      this.setActiveLink();
    });

    this.setActiveLink();
  }

  setActiveLink() {
    const currentUrl = this.router.url;
    // console.log(currentUrl);

    for (const group of this.sidebarItems) {
      for (const item of group.items) {
        if (item.link === currentUrl || currentUrl.startsWith(item.link + '/')) {
          this.activeLink = { title: item.title, icon: item.icon };
          return;
        }
      }
    }

    this.activeLink = undefined;
  }

  /** Only vendors have a shop name/logo to show; everyone else shows their role. */
  get isVendor(): boolean {
    return this.authService.hasPermission(Permissions.IS_VENDOR);
  }

  getUserRole(): string | null {
    if (this.JwtPayload) {
      switch (this.JwtPayload.role) {
        case 'admin':
          return 'nav.roles.admin';
        case 'vendor':
          return 'nav.roles.vendor';
        case 'delivery_admin':
          return 'nav.roles.delivery_admin';
        default:
          return null;
      }
    }

    return null;
  }

  toggleDropdown() {
    this.isLanguageOpen = false;
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleLanguageDropdown() {
    this.isDropdownOpen = false;
    this.isLanguageOpen = !this.isLanguageOpen;
  }

  changeLanguage(lang: SupportedLanguage) {
    this.languageService.changeLanguage(lang);
    this.isLanguageOpen = false;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
      this.isLanguageOpen = false;
    }
  }

  logout() {
    this.authApiService.logout().subscribe({
      next: (response) => {
        this.authService.logout();
      },
      error: (err) => {
        this.toastNotificationService.createNotification(
          this.translate.instant('common.errors.generic_request_failed'),
          ToastNotificationType.ERROR,
        );
      }
    });
  }

}
